import { Add, Delete, Search } from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi, ApiError } from '@/infrastructure/services/api-client';
import type { AllowedDomainView } from '@/infrastructure/services/api-client';
import { useNotificationStore } from '@/stores/use-notification-store';
import { useThemeStore } from '@/stores/use-theme-store';

export function AdminDomainsPage() {
    const { t } = useTranslation();
    const { addNotification } = useNotificationStore();
    const { mode } = useThemeStore();

    const [domains, setDomains] = useState<AllowedDomainView[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [newDomain, setNewDomain] = useState('');
    const [addLoading, setAddLoading] = useState(false);
    const [removeTarget, setRemoveTarget] = useState<AllowedDomainView | null>(null);
    const [removeLoading, setRemoveLoading] = useState(false);

    const fetchDomains = useCallback(async () => {
        try {
            const result = await adminApi.listDomains();
            setDomains(result.domains);
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            }
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    useEffect(() => {
        void fetchDomains();
    }, [fetchDomains]);

    const filteredDomains = useMemo(() => {
        if (!search.trim()) {
            return domains;
        }
        const q = search.toLowerCase();
        return domains.filter((d) => d.domain.toLowerCase().includes(q));
    }, [domains, search]);

    const handleAdd = async () => {
        const trimmed = newDomain.trim().toLowerCase();
        if (!trimmed) {
            return;
        }

        setAddLoading(true);
        try {
            const created = await adminApi.addDomain(trimmed);
            setDomains((prev) => [...prev, created].sort((a, b) => a.domain.localeCompare(b.domain)));
            setNewDomain('');
            addNotification(t('admin.domains.added'), 'success');
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            }
        } finally {
            setAddLoading(false);
        }
    };

    const handleRemove = async () => {
        if (!removeTarget) {
            return;
        }
        setRemoveLoading(true);
        try {
            await adminApi.removeDomain(removeTarget.id);
            setDomains((prev) => prev.filter((d) => d.id !== removeTarget.id));
            addNotification(t('admin.domains.removed'), 'success');
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            }
        } finally {
            setRemoveLoading(false);
            setRemoveTarget(null);
        }
    };

    const formatDate = (dateStr: string) =>
        new Date(dateStr).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {t('admin.domains.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('admin.domains.subtitle')}
                </Typography>
            </Box>

            <Card
                sx={{
                    mb: 3,
                    border: '1px solid',
                    borderColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                }}
            >
                <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', py: 2, '&:last-child': { pb: 2 } }}>
                    <TextField
                        size="small"
                        placeholder={t('admin.domains.domainPlaceholder')}
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                void handleAdd();
                            }
                        }}
                        sx={{ flexGrow: 1 }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        disabled={addLoading || !newDomain.trim()}
                        onClick={() => void handleAdd()}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        {t('admin.domains.add')}
                    </Button>
                </CardContent>
            </Card>

            <Card
                sx={{
                    border: '1px solid',
                    borderColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                }}
            >
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Box sx={{ p: 2, pb: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder={t('admin.domains.search')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Box>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('admin.domains.domain')}</TableCell>
                                    <TableCell>{t('admin.domains.createdAt')}</TableCell>
                                    <TableCell align="right">{t('admin.domains.actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('admin.domains.loading')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredDomains.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('admin.domains.empty')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredDomains.map((domain) => (
                                        <TableRow key={domain.id} hover>
                                            <TableCell sx={{ fontWeight: 500 }}>{domain.domain}</TableCell>
                                            <TableCell>{formatDate(domain.createdAt)}</TableCell>
                                            <TableCell align="right">
                                                <Tooltip title={t('admin.domains.remove')}>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => setRemoveTarget(domain)}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            <Dialog open={!!removeTarget} onClose={() => !removeLoading && setRemoveTarget(null)}>
                <DialogTitle>{t('admin.domains.confirmTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('admin.domains.confirmRemove', { domain: removeTarget?.domain })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemoveTarget(null)} disabled={removeLoading}>
                        {t('admin.domains.cancel')}
                    </Button>
                    <Button color="error" onClick={() => void handleRemove()} disabled={removeLoading}>
                        {t('admin.domains.confirmRemoveBtn')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default AdminDomainsPage;
